import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardPayment } from '@mercadopago/sdk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addCreditCard } from '@/lib/payment-methods';
import { useMember } from '@/lib/members/hooks/useMember.ts';
import LoadingBar from '@/components/common/loading-bar.tsx';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';
import { InfoIcon } from 'lucide-react';
import {
  CLUB_PERSONAL_PAY_ID,
  MP_CARD_VALIDATION_AMOUNT,
} from '@/lib/config.ts';
import { isAxiosError } from 'axios';

type CardFormData = {
  token: string;
  payment_method_id: string;
  issuer_id: string;
  installments: number;
  transaction_amount: number;
};

export type TPaymentMethodFormProps = {
  header?: ReactNode;
  submitButtonText?: string;
  onSubmitted?: () => Promise<void>;
  onPendingChange?: (isPending: boolean) => void;
};

export function PaymentMethodForm({
  header,
  submitButtonText,
  onSubmitted = async () => {},
  onPendingChange,
}: TPaymentMethodFormProps) {
  const queryClient = useQueryClient();
  const { member, reload } = useMember();
  const [brickKey, setBrickKey] = useState(0);

  const { mutateAsync: saveCard, isPending } = useMutation({
    mutationFn: async (cardData: {
      token: string;
      paymentMethodId: string;
      issuerId?: string;
    }) => {
      await addCreditCard({
        token: cardData.token,
        paymentMethodId: cardData.paymentMethodId,
        issuerId: cardData.issuerId,
        deviceSessionId: window.MP_DEVICE_SESSION_ID?.trim() || undefined,
        idempotencyKey: crypto.randomUUID(),
      });
    },
    onSuccess: async () => {
      await reload();
      await queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      toast.success('Tarjeta agregada correctamente');
      await onSubmitted();
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 422) {
        const errors: Record<string, string[]> = error.response.data?.errors ?? {};
        const messages = Object.values(errors).flat();
        const message = messages.length > 0 ? messages.join(' ') : 'No se pudo agregar la tarjeta.';
        toast.error(message);
      } else {
        toast.error('No se pudo agregar la tarjeta. Intenta nuevamente.');
      }
      setBrickKey((k) => k + 1);
    },
  });

  useEffect(() => {
    onPendingChange?.(isPending);
  }, [isPending, onPendingChange]);

  // Keep saveCard in a ref so the onSubmit callback passed to the brick always
  // has the latest version without needing to be re-declared (which would remount the brick).
  const saveCardRef = useRef(saveCard);
  useEffect(() => {
    saveCardRef.current = saveCard;
  });

  const isPersonalPay = member?.clubId
    ? parseInt(member?.clubId) === CLUB_PERSONAL_PAY_ID
    : false;

  // IMPORTANT: all props passed to <CardPayment> must be stable references.
  // The SDK's useEffect depends on [initialization, customization, onReady, onError, onSubmit],
  // so any new reference triggers unmount + remount of the brick, destroying the secure field
  // iframes mid-tokenization and causing "No primary field found" errors.

  const brickInitialization = useMemo(
    () => ({
      amount: MP_CARD_VALIDATION_AMOUNT,
      // Pre-filling the email hides the field in the brick, since the user is already logged in.
      payer: { email: member?.Email },
    }),
    [member?.Email],
  );

  const brickCustomization = useMemo(
    () => ({
      paymentMethods: {
        maxInstallments: 1,
        creditCard: 'all',
        debitCard: [],
        prepaidCard: [],
        ticket: [],
        bankTransfer: [],
        atm: [],
        mercadoPago: [],
      },
      visual: {
        hideFormTitle: true,
        // The brick renders inside an iframe, so it cannot access the page's CSS
        // variables. Primary color values from index.css are inlined here.
        style: {
          theme: 'flat',
          customVariables: {
            baseColor: 'hsl(324, 100%, 40%)',           // --primary
            baseColorFirstVariant: 'hsl(324, 100%, 30%)', // --primary-hover
            buttonTextColor: 'hsl(355.7, 100%, 97.3%)',  // --primary-foreground
          },
        },
        texts: { formSubmit: submitButtonText ?? 'Guardar' },
      },
    }),
    [submitButtonText],
  );

  // Called by the brick after it validates and tokenizes the card data internally.
  const handleBrickSubmit = useCallback(async (cardFormData: CardFormData) => {
    await saveCardRef.current({
      token: cardFormData.token,
      paymentMethodId: cardFormData.payment_method_id,
      issuerId: cardFormData.issuer_id,
    });
  }, []);

  const handleBrickError = useCallback((error: unknown) => {
    console.error('Brick error:', error);
  }, []);

  if (!member) {
    return <LoadingBar />;
  }

  return (
    <div className="w-full">
      <div className="m-auto flex flex-col space-y-4">
        {!!header && <div>{header}</div>}
        {isPersonalPay && (
          <Alert>
            <InfoIcon className="h-6 w-6" />
            <AlertDescription>
              Solo se aceptan tarjetas <strong>Personal Pay</strong> como medio
              de pago para este Club.
            </AlertDescription>
          </Alert>
        )}
        {/*
         * MP Card Payment Brick — renders PCI-compliant card fields in iframes.
         * The brick's built-in submit button handles validation, tokenization, and
         * its own loading state. We receive the token in onSubmit and save to backend.
         *
         * IMPORTANT: all props must be stable references (useCallback/useMemo).
         * The SDK remounts the brick — and destroys the secure field iframes — whenever
         * any prop reference changes, causing "No primary field found" on tokenization.
         */}
        <CardPayment
          key={brickKey}
          initialization={brickInitialization}
          customization={brickCustomization}
          onSubmit={handleBrickSubmit}
          onError={handleBrickError}
        />
      </div>
    </div>
  );
}
